import { PropsWithChildren, useMemo } from 'react';
import { DevCycleProvider } from '@devcycle/react-client-sdk';

import { DEFAULT_SPACE_ID } from '../Configuration';
import { ContentService } from '../ContentService';
import { CMSContent, CMSProviderContext } from '../contexts/CMSContext';
import { Environment } from '../Environment';
import { useAsyncData } from '../hooks';
import { opcosFallback, MaintenanceBannerData, ModalData, OpCoData } from '../models';
import { useCookies } from '../../../../hooks/useCookies';

export type CMSProviderProps = {
  /** Access token, must have access to provided environment on space. Must be a Content Delivery Token. **DO NOT USE A CONTENT MANAGEMENT TOKEN** */
  accessToken: string;
  environment: Environment;
  /** Can be provided to override default space ID as required */
  space?: string;
};

const DECYCLE_ENVIRONMENTS: Record<Environment, string> = {
  [Environment.DEV]: import.meta.env.VITE_DEVCYCLE_SDK_KEY_DEV,
  [Environment.TESTING]: import.meta.env.VITE_DEVCYCLE_SDK_KEY_TST,
  [Environment.STAGING]: import.meta.env.VITE_DEVCYCLE_SDK_KEY_STG,
  [Environment.PROD]: import.meta.env.VITE_DEVCYCLE_SDK_KEY_PRD,
};

const MAINTENANCE_BANNER_FALLBACK: Array<MaintenanceBannerData> = [];

export const CMSProvider = ({ accessToken, children, environment, space = DEFAULT_SPACE_ID }: PropsWithChildren<CMSProviderProps>) => {
  const { locale } = useCookies();

  // Important that this is memoed correctly as creating a new client
  // will cause refreshes of data automatically (env change/entity ID changes/space/access token)
  const service = useMemo(() => {
    return new ContentService({ accessToken, environment, space, locale });
  }, [accessToken, environment, space, locale]);

  const fallbacks = opcosFallback as Record<string, Array<OpCoData>>;

  const opcos = useAsyncData<Array<OpCoData>>({ fetcher: service.getOpCos, fallback: locale in fallbacks ? fallbacks[locale] : fallbacks['en-GB'] });
  const maintenanceBanners = useAsyncData<MaintenanceBannerData[]>({ fetcher: service.getMaintenanceBanners, fallback: MAINTENANCE_BANNER_FALLBACK });
  const modal = useAsyncData<ModalData | undefined>({ fetcher: service.getModal, fallback: undefined });

  const context = useMemo((): CMSContent => ({ opcos, maintenanceBanners, modal }), [opcos, maintenanceBanners, modal]);

  return (
    <CMSProviderContext.Provider value={context}>
      <DevCycleProvider config={{ sdkKey: DECYCLE_ENVIRONMENTS[environment] }}>{children}</DevCycleProvider>
    </CMSProviderContext.Provider>
  );
};
